import { Children } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { media } from '@myrealtrip/modash';
import { StatusTag, Text } from '@myrealtrip/web-ui';
import { IcoCheck } from '@myrealtrip/web-icons';
import { WishMark } from '@myrealtrip/wish';

import Show from '~components/Show';
import ReviewRating from '~components/ReviewRating';

import type { TransportationProduct } from '~models/Transportation/PointToPointTicket';
import { PASS_TAG_MAP } from '~constants/pass';

interface Props {
  product: TransportationProduct;
}

const Summary = ({ product }: Props) => {
  const {
    gid,
    descriptions = [],
    tags = [],
    title = '',
    reviewRating = 0,
    reviewCount = 0,
  } = product ?? {};

  const { colors } = useTheme();

  return (
    <Container>
      <MainInfoWrapper>
        <TextInfoWrapper>
          <TagWrapper>
            {tags.map(({ label, value }) => (
              <StatusTag
                key={value}
                label={label}
                variant={PASS_TAG_MAP[value].variant}
                tint={PASS_TAG_MAP[value].tint}
              />
            ))}
          </TagWrapper>
          <Show when={title}>
            <Title as="h3" typography="headlineBold18" lineClamp={2}>
              {title}
            </Title>
          </Show>
          <ReviewRatingContainer
            hasReview={!!reviewRating && Number(reviewRating) > 0 && reviewCount > 0}
          >
            <Show when={reviewRating && Number(reviewRating) > 0 && reviewCount > 0}>
              <ReviewRating rating={Number(reviewRating)} count={reviewCount} />
            </Show>
          </ReviewRatingContainer>
        </TextInfoWrapper>
        <WishContainer>
          <WishMark id={gid} showCount icoBookmarkLineColor={colors.gray.s400} />
        </WishContainer>
      </MainInfoWrapper>
      <Description>
        {Children.toArray(
          descriptions.map((description) => (
            <DescriptionItem>
              <DescriptionIcon size={16} color={colors.blue.s500} />
              <DescriptionText>{description}</DescriptionText>
            </DescriptionItem>
          )),
        )}
      </Description>
    </Container>
  );
};

export default Summary;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin: 0 0 20px;

  ${media.minMobile} {
    margin: 0 0 24px;
  }
`;

const MainInfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const TextInfoWrapper = styled.div``;

const TagWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 8px;
`;

const Title = styled(Text)`
  margin: 0;
`;

const ReviewRatingContainer = styled.div<{ hasReview: boolean }>`
  margin-top: ${({ hasReview }) => (hasReview ? 6 : 0)}px;

  ${media.minMobile} {
    min-height: 19px;
    margin-top: 8px;
  }
`;

const WishContainer = styled.div`
  margin-left: 12px;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const DescriptionItem = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 1.5px;
`;

const DescriptionIcon = styled(IcoCheck)`
  margin-top: 1.5px;
`;

const DescriptionText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s700};

  ${({ theme }) => theme.typography.paragraphNormal14};
`;
